<script setup>
import { HrBtn } from '../primitives';

/**
 * Cancel or dismiss action — the low-emphasis counterpart to HrConfirmBtn.
 *
 * @status recommended
 * @group actions
 * @useWhen Canceling a dialog or form without committing changes
 * @related HrConfirmBtn
 * @a11y Native button; activates on Enter and Space.
 */
defineOptions({ name: 'HrDismissBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

defineProps({
  /**
   * Button text label. When null the default slot provides the content.
   * @replacesSlot default
   */
  label: {
    type: String,
    default: null,
  },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders a pill-shaped skeleton placeholder instead of the button. */
  pending: {
    type: Boolean,
    default: false,
  },
  /** Vue Router route location. Renders the button as a router-link. */
  to: {
    type: [String, Object],
    default: null,
  },
  /** Replace the current history entry instead of pushing a new one. */
  replace: {
    type: Boolean,
    default: false,
  },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: {
    type: String,
    default: null,
  },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: {
    type: String,
    default: null,
  },
});

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = { flat: true, color: 'secondary' };
</script>
<template>
  <hr-btn
    variant="text"
    :legacy-props="legacyProps"
    v-bind="$props"
    @click="emit('click', $event)"
  >
    <!-- @slot Button label / content. -->
    <slot />
  </hr-btn>
</template>
